import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalleryDto, GalleryPublicAccessLevel, TenantDto } from '@picflow/types';
import { Subscription, firstValueFrom } from 'rxjs';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { debounce } from '../../../lib/debounce';
import { getFormControl } from '../../../lib/get-form-control';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AccessLevelService } from '../../../services/access-level.service';
import { GalleryService } from '../../../services/gallery.service';
import { ToastService } from '../../../services/toast.service';
import { GalleryShareContextComponent } from '../../../shared-extra/components/gallery-share-context/gallery-share-context.component';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { ProTagComponent } from '../../../shared-extra/components/pro-tag/pro-tag.component';
import { CopyClipboardDirective } from '../../../shared-extra/directives/copy-to-clipboard';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { EditableGalleryUrlFieldComponent } from '../editable-gallery-url-field/editable-gallery-url-field.component';
import { ShareGallerySocialLinksComponent } from '../share-gallery-social-links/share-gallery-social-links.component';

@Component({
  selector: 'app-share-gallery-link',
  templateUrl: './share-gallery-link.component.html',
  styleUrls: ['./share-gallery-link.component.scss'],
  standalone: true,
  imports: [
    EditableGalleryUrlFieldComponent,
    SvgComponent,
    TransitionPreventorDirective,
    FormsModule,
    GalleryShareContextComponent,
    ProTagComponent,
    InputFieldComponent,
    InputPostDirective,
    ButtonDirective,
    CopyClipboardDirective,
    ReactiveFormsModule,
    ShareGallerySocialLinksComponent,
    BillingScopePipe,
    AutofocusDirective,
    ClickTrackDirective,
    SwitchComponent,
  ],
})
export class ShareGalleryLinkComponent implements OnInit, OnDestroy {
  private galleryService = inject(GalleryService);
  private toasts = inject(ToastService);
  private accessLevelService = inject(AccessLevelService);
  @Input() gallery: GalleryDto;
  accessLevel: GalleryAccessLevel;
  isEditingPath = false;
  loading = true;
  isGalleryGuest: boolean;
  publicAccessLevel: GalleryPublicAccessLevel;
  publicAccessLevels: GalleryPublicAccessLevel[] = ['review', 'view_only'];
  addingPassword = false;
  passwordForm = new FormGroup({
    password: getFormControl('gallery-password'),
  });
  updateSub: Subscription;
  showPassword: boolean;
  allowAccess: boolean;
  tenant: TenantDto;

  private analytics = inject(PICFLOW_ANALYTICS);

  async ngOnInit() {
    this.updateSub = this.galleryService.onGalleryUpdated$.subscribe(gallery => {
      if (!gallery || !this.gallery) return;
      if (gallery.id !== this.gallery.id) return;
      const tenant = this.gallery.tenant;
      this.gallery = Object.assign(this.gallery, gallery);
      this.gallery.tenant = tenant;
      this.refreshDataDebounced();
    });

    this.tenant = this.gallery.tenant as TenantDto;
    this.allowAccess = this.gallery.public_access !== 'no_access' && !this.tenant.block_public_link_access;
    this.showPassword = this.gallery.password_enabled;
    this.passwordForm.setValue({ password: this.gallery['password'] || '' });
    await this.refreshData();
    this.loading = false;
  }

  private async refreshData() {
    const [isGalleryGuest, accessLevels, accessLevel] = await Promise.all([
      firstValueFrom(this.accessLevelService.isGalleryGuest(this.gallery)),
      this.accessLevelService.getAccessLevels(this.gallery),
      this.accessLevelService.getAccessLevel(this.gallery),
    ]);
    this.publicAccessLevel = accessLevels[1];

    this.accessLevel = accessLevel;
    this.isGalleryGuest = isGalleryGuest;
    this.loading = false;
  }

  refreshDataDebounced = debounce(() => {
    this.refreshData();
  }, 1000);

  reportAddPassword() {
    this.analytics.track('add_gallery_password');
  }

  onPasswordCopied() {
    this.toasts.add('Password Copied', 'success');
    this.analytics.track('gallery_password_copied');
  }

  removePassword() {
    this.analytics.track('remove_gallery_password');
    this.gallery.password_enabled = false;
    this.galleryService.updateGallery(this.gallery.id, {
      password_enabled: false,
    });
  }

  async changePublicAccess(accessLevel: GalleryPublicAccessLevel) {
    this.gallery.public_access = accessLevel;
    this.publicAccessLevel = accessLevel;
    await this.galleryService.updateGallery(this.gallery.id, { public_access: accessLevel });
  }

  savePassword() {
    if (!this.passwordForm.valid) return;
    this.addingPassword = false;
    const password = this.passwordForm.value.password;
    if (!password) {
      this.passwordForm.setValue({ password: this.gallery['password'] });
      return;
    }
    this.gallery.password_enabled = true;
    this.gallery['password'] = password;
    this.galleryService.updateGallery(this.gallery.id, {
      password: password,
      password_enabled: true,
    });
  }

  async togglePassword() {
    if (!this.showPassword && this.gallery.password_enabled) {
      this.gallery.password_enabled = false;
      this.galleryService.updateGallery(this.gallery.id, { password_enabled: false });
    } else {
      this.gallery.password_enabled = true;
      if (this.gallery['password']) {
        this.galleryService.updateGallery(this.gallery.id, {
          password_enabled: true,
          password: this.gallery['password'],
        });
        this.addingPassword = false;
      } else {
        this.addingPassword = true;
      }
    }
  }

  togglePublicAccess() {
    if (this.allowAccess) {
      this.changePublicAccess('review');
    } else {
      this.changePublicAccess('no_access');
    }
  }

  saveRequiresIdentification() {
    this.galleryService.updateGallery(this.gallery.id, {
      requires_user_identification: this.gallery.requires_user_identification,
    });
  }

  ngOnDestroy(): void {
    this.updateSub.unsubscribe();
  }
}
